/**
 * Copyright © 2025-2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

// The three ways a customer's Xero transactions can be pushed to Zamp - Standard calculates tax and
// posts, Post only posts without calculating (for customers whose channel already collected tax
// elsewhere), No Posting sends nothing to Zamp at all. Mirrors the backend's
// Zamp_Xero_Api.Models.CustomerEntityMapping.PostingOption constants - "value" is what's saved/sent
// to the API, "label" is the display text shown in the UI (matches the feature's mockup). Shared
// between customerentity_mapping.jsx (per-customer choice, including its Excel import/export and
// text filter) and zamp_settings.jsx (tenant-level default for newly-discovered customers) so every
// surface stays in sync with the backend's constants.
export const POSTING_OPTIONS = [
    { value: "Standard", label: "Standard" },
    { value: "PostOnly", label: "Post only" },
    { value: "NoPosting", label: "No Posting" },
];

export const DEFAULT_POSTING_OPTION = POSTING_OPTIONS[0].value;

/**
 * Display label for a stored posting-option value. Falls back to the default option's label for a
 * null/blank/unrecognized value, mirroring the backend's PostingOption.Normalize so a row saved
 * before this field existed reads as "Standard" rather than blank.
 *
 * @param {string} [value] canonical value as persisted by the API (e.g. "PostOnly").
 * @returns {string} the label to render (e.g. "Post only").
 */
export const getPostingOptionLabel = (value) =>
    POSTING_OPTIONS.find(option => option.value === value)?.label ?? POSTING_OPTIONS[0].label;

/**
 * Resolves free-form text from an imported spreadsheet back to a canonical posting-option value.
 * Accepts either the display label ("Post only") or the canonical value ("PostOnly"), ignoring case
 * and surrounding whitespace, so a file exported by this app round-trips and a hand-typed cell is
 * still forgiving. Returns undefined for anything unrecognized (including blank) - callers decide
 * whether that means "leave the row unchanged" or "report an import error".
 *
 * @param {string} [text] raw cell text.
 * @returns {string|undefined} the canonical value, or undefined when not recognized.
 */
export const resolvePostingOptionValue = (text) => {
    const needle = String(text ?? "").trim().toLowerCase();
    if (!needle) return undefined;
    return POSTING_OPTIONS.find(
        option => option.value.toLowerCase() === needle || option.label.toLowerCase() === needle
    )?.value;
};
